/**
 * 子站自訂網域的登入交接。
 *
 * 子站可以掛在自己的網域（例如 school.king-fong.com），header 也跟平台網域那份一樣有會員區。
 * 但登入狀態存在瀏覽器裡、依網域分開：在 taro.kf-9595.com 登入過，到子站網域還是未登入；
 * 子站網域上也沒有登入頁、會員中心這些平台頁面（路由只有自訂頁，見 router 的 customDomainRoutes）。
 *
 * 所以登入一律在平台網域做，做完用一組「交接碼」把登入狀態帶回子站網域：
 *
 *   1. 子站網域按「登入」→ 平台 /auth/handoff?return=子站網址（還沒登入就先走一般登入頁）
 *   2. 平台領一組交接碼，轉回「子站網址#taro-handoff=交接碼」
 *   3. 子站網域一啟動就把交接碼從網址收走，拿去換自己的 access token（router 的 syncSession）
 *
 * 交接碼 60 秒失效、只能用一次、只能從當初指定的子站網域換（後端 TokenController）。
 * 放在 # 後面：不會送到伺服器，也不會被 GA 當成網址的一部分記下來。
 *
 * 會員區其他項目（會員中心、商品中心、通知…）一樣到平台網域開 —— 那邊本來就是登入狀態。
 */
import { isCustomDomain } from "./custom-domain";

/** 平台網域，例如 https://taro.kf-9595.com */
export const PLATFORM_BASE = String(process.env.VUE_APP_MAIN_BASE_URL || "").replace(/\/+$/, "");

/** 平台上的交接頁 */
export const HANDOFF_PATH = "/auth/handoff";

const HASH_KEY = "taro-handoff";

let pendingCode = null;

/**
 * 把網址上的 #taro-handoff=… 收起來，並從網址拿掉。
 *
 * **一定要在建立 router 之前呼叫**：router 建立時就會讀網址，# 留著會被
 * scrollBehavior 當成選擇器去找元素（`#taro-handoff=xxx` 不是合法選擇器，會丟例外）。
 * 拿掉之後重新整理、複製網址、按上一頁，都不會再看到交接碼。
 */
export function takeHandoffCodeFromUrl() {
  const match = /^#taro-handoff=([A-Za-z0-9_-]{16,100})$/.exec(window.location.hash || "");
  if (!match) return;

  pendingCode = match[1];
  try {
    window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
  } catch (e) {
    /* 舊瀏覽器不支援就算了，交接碼只能用一次，留在網址上也換不到東西 */
  }
}

/** 取出還沒換的交接碼（取一次就清掉） */
export function consumeHandoffCode() {
  const code = pendingCode;
  pendingCode = null;
  return code;
}

/** 回到子站網址時帶上交接碼。原本的 # 會被換掉 —— 自訂頁的定位用的是 ?share=，不是 # */
export function withHandoffCode(returnUrl, code) {
  const url = new URL(returnUrl);
  url.hash = `${HASH_KEY}=${code}`;
  return url.toString();
}

/**
 * 交接頁的回程網址先在前端擋一次明顯不對的（後端還會再檢查是不是登記過的子站網域）。
 * 正式環境只收 https；本機開發允許 http。
 */
export function isPlausibleReturnUrl(value) {
  if (!value) return false;
  try {
    const url = new URL(value);
    if (url.protocol === "https:") return true;
    return process.env.NODE_ENV !== "production" && url.protocol === "http:";
  } catch (e) {
    return false;
  }
}

/** 網址參數取單一字串值（同名多值取第一個） */
function pickRefc(refc) {
  const raw = Array.isArray(refc) ? refc[0] : refc;
  return typeof raw === "string" && raw.trim() ? raw.trim() : "";
}

const REFC_KEY = "taro-site-refc";

/**
 * 從分享連結（子站網址?refc=…）進來時記住推薦碼。
 *
 * 子站內換頁網址上就沒有 ?refc= 了，訪客逛到別頁才按「註冊」，推薦碼會掉 ——
 * 會員分享改指向子站網域之後，這是推薦人能不能掛上的關鍵。存在 sessionStorage：
 * 只跟著這個分頁，關掉就沒了，不會把很久以前點過的推薦碼套到之後的註冊上。
 */
export function rememberReferralFromUrl() {
  try {
    const refc = new URLSearchParams(window.location.search).get("refc");
    if (refc && refc.trim()) window.sessionStorage.setItem(REFC_KEY, refc.trim());
  } catch (e) {
    /* 無痕模式等存不了就算了，網址上有的時候照樣帶得到 */
  }
}

/** 這個分頁進站時記下的推薦碼（rememberReferralFromUrl）；登入對話框用 */
export function rememberedReferral() {
  return rememberedRefc();
}

function rememberedRefc() {
  try {
    return window.sessionStorage.getItem(REFC_KEY) || "";
  } catch (e) {
    return "";
  }
}

/**
 * 子站網域上按「登入」或「註冊」：到平台登入，登入完回到 returnUrl 並帶入登入狀態。
 *
 * @param {object} [options]
 * @param {"SignIn"|"SignUp"} [options.status] 平台登入頁要開哪個分頁
 * @param {string} [options.returnUrl] 登入完回到哪裡，預設目前這一頁
 * @param {string} [options.refc] 推薦碼。子站網址上帶著 ?refc= 進來的訪客，註冊時要掛到推薦人底下；
 *   沒給就用進站時記下的那個（rememberReferralFromUrl）
 */
export function platformSignInUrl({ status = "SignIn", returnUrl = window.location.href, refc } = {}) {
  const url = new URL(PLATFORM_BASE + HANDOFF_PATH);
  url.searchParams.set("return", returnUrl);
  url.searchParams.set("status", status === "SignUp" ? "SignUp" : "SignIn");
  const referral = pickRefc(refc) || rememberedRefc();
  if (referral) url.searchParams.set("refc", referral);
  return url.toString();
}

/** 子站網域上登出：平台那邊也登出，再回到 returnUrl */
export function platformSignOutUrl(returnUrl = window.location.href) {
  const url = new URL(PLATFORM_BASE + HANDOFF_PATH);
  url.searchParams.set("action", "logout");
  url.searchParams.set("return", returnUrl);
  return url.toString();
}

let platformResolver = null;

/**
 * 平台頁面在平台網域上的完整網址。location 跟 $router.push 收的一樣（名稱或路徑）。
 *
 * 子站網域的 router 只有自訂頁，用名稱解析不出平台的頁面，所以另外拿平台的路由表
 * 建一個不接網址列的 router 專門解析。用到才建，而且 require 放在函式裡 ——
 * router 本身也 import 這個檔案，寫在檔案開頭會變成循環引用。
 */
export function platformHref(location) {
  if (!platformResolver) {
    const VueRouter = require("vue-router").default;
    const { platformRoutes } = require("@/router");
    platformResolver = new VueRouter({ mode: "abstract", routes: platformRoutes });
  }
  return PLATFORM_BASE + platformResolver.resolve(location).href;
}

/**
 * 會員區的導航：平台網域照常走 router，子站網域到平台網域開同一頁。
 *
 * @param {import("vue-router").default} router 元件的 this.$router
 * @param {string|object} location 跟 $router.push 收的一樣
 */
export function navigateMemberArea(router, location) {
  if (!isCustomDomain()) {
    router.push(location).catch(() => {});
    return;
  }
  window.location.href = platformHref(location);
}
